import React, { FC, useContext, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { AccordionContainer, Accordion, IconToggle, LoadingSpinner, SearchBox, TooltipWrapper } from "@app/shared";
import { TableType, ViewableField } from "@app/viewable-fields";
import { GainsPercentEnum, NPF, NetNewServicingProviderTableData } from "@app/projects/servicing-provider/types";
import { EntityTypeEnum, LocationConfidenceScoresLabelEnum, NetworkAssessmentAnalysisType, ProjectPerspective, ProjectionProviderType, ProviderNetworkType } from "@app/projects/enumerators";
import { SortDirections, wafflesKeyColumnName } from "@app/shared/waffles";
import { CountyProjectContext, Project, ProjectRunConfigurationResult, RegionFilterItem } from "@app/projects";
import { buildCommonImpactProviderColumns } from "@app/projects/servicing-provider/ServicingProviderHelpers";
import { RoleRestricted, Roles, UserContext } from "@app/user";
import { DeleteProjectionProvider, ProjectionPanelMode, ProjectionProviderDetails } from "@app/projects/projection-provider";
import { Flyout, FlyoutContext, FlyoutLink } from "@app/shared/Flyouts";
import { capitalizeFirstLetter } from "@app/shared/utility";
import { Link } from "react-router-dom";
import { QESButton } from "@app/shared/qes-standards/Button";
import { RegionGranularity } from "@app/region-sets";
import { ExportProviderAvailabilityType, ExportType } from "@app/exports/enumerations";
import { GeographyService } from "@app/geography";
import { toast } from "@app/projects/servicing-provider/DistanceResultToast";
import { uniqueId } from "lodash";
import AdequacyFilters from "@app/projects/network-assessment/adequacy/adequacyFilters";
import GainsPercentFilter from "@app/projects/servicing-provider/provider-claims/GainsPercentFilter";
import AccessSummary from "@app/projects/results/adequacy/accessSummary";
import NetNewServicingProviderService from "@app/projects/servicing-provider/NetNewServicingProviderService";
import { NetNewProvider } from "@app/projects/results/servicing-provider/NetNewProvider";
import { NetNewServicingProviderTable } from "@app/projects/servicing-provider/NetNewServicingProviderTable";
import { SimulateProviderTargeting } from "@app/projects/servicing-provider/SimulateProviderTargeting";
import ProviderClaimsFilters from "@app/projects/servicing-provider/provider-claims/ProviderClaimsFilters";
import { EffectivenessEfficiencyValueEnum } from "@app/projects/servicing-provider/provider-claims/EffectivenessEfficiencyValueEnum";
import { MotiveScoreValueEnum } from "@app/projects/servicing-provider/provider-claims/MotiveScoreValueEnum";
import { ClaimsActivityLineOfBusinessTypeEnum, ClaimsActivityVolumeLabelEnum } from "@app/projects/network-assessment/pci-dashboard/claims-activity";
import ServicingProviderProfile from "@app/projects/servicing-provider/ServicingProviderProfile";
import { ExportDataRouteState } from "@app/exports/exportsRouteState";
import { ReportCustomizationRouteState } from "@app/report-customization/build-reports/interfaces/reportCustomizationRouteState";
import { NewPatientClaimsEnum } from "@app/projects/servicing-provider/provider-claims/NewPatientClaimsEnum";
import { FeatureFlagContext, Features } from "@app/feature-flags";
import projectionProviderDetailsReducer, {
  projectionProviderDetailsInitialState
} from "@app/projects/projection-provider/projectionProviderDetailsReducer";
import { ReportType } from "@app/report-customization/build-reports/interfaces/reportTypeEnum";
import { ProjectionProvider } from "@app/projects/results/projection-provider";
import FlyoutContextProvider from "@app/shared/Flyouts/FlyoutContext";

type DisplayedProviderType = "targeted" | "nonTargeted";

interface NetNewServicingProvidersPanelProps {
  accessSummary: AccessSummary
  editRestricted: boolean;
  networkAssessmentAnalysisType: NetworkAssessmentAnalysisType;
  processProviderChanges: (doItNow: boolean) => void;
  project: Project;
  providerType: ProviderNetworkType;
  readOnly: boolean;
  regionSetId: number;
  runConfiguration: ProjectRunConfigurationResult;
  setProjectHasUnprocessedProjectionProviders: (doesIt: boolean) => void;
  specialtyGroupCode: string;
  viewableFields: ViewableField[];
  exportEndpoint: string;
}

const NetNewServicingProvidersPanel: FC<React.PropsWithChildren<NetNewServicingProvidersPanelProps>> = (props: NetNewServicingProvidersPanelProps) => {
  const [displayedProviderType, setDisplayedProviderType] = useState<DisplayedProviderType>("targeted");
  const [displayedProviderTypeChanged, setDisplayedProviderTypeChanged] = useState<boolean>(false);
  const [displayedProviderTypeDefault, setDisplayedProviderTypeDefault] = useState<boolean>(false);
  const [displayedProviderData, setDisplayedProviderData] = useState<NetNewServicingProviderTableData[]>(undefined);
  const [lastSearchValue, setLastSearchValue] = useState("");
  const [selectedColumn, setSelectedColumn] = useState<string>(undefined);
  const [sortDirection, setSortDirection] = useState<SortDirections>(undefined);
  const [selectedEffectivenessValues, setSelectedEffectivenessValues] = useState<EffectivenessEfficiencyValueEnum[]>(undefined);
  const [selectedEfficiencyValues, setSelectedEfficiencyValues] = useState<EffectivenessEfficiencyValueEnum[]>(undefined);
  const [selectedVolumeLabels, setSelectedVolumeLabels] = useState<ClaimsActivityVolumeLabelEnum[]>(undefined);
  const [selectedLineOfBusiness, setSelectedLineOfBusiness] = useState<ClaimsActivityLineOfBusinessTypeEnum>(undefined);
  const [selectedLocationConfidenceValues, setSelectedLocationConfidenceValues] = useState<LocationConfidenceScoresLabelEnum[]>(undefined);
  const [selectedMotiveAppropriatenessValues, setSelectedMotiveAppropriatenessValues] = useState<MotiveScoreValueEnum[]>(undefined);
  const [selectedNewPatientClaimsValues, setSelectedNewPatientClaimsValues] = useState<NewPatientClaimsEnum[]>(undefined);
  const [totalRecords, setTotalRecords] = useState(0);
  const [skip, setSkip] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedProviders, setSelectedProviders] = useState<string[]>([]);
  const netNewServicingProviderService = useRef(new NetNewServicingProviderService()).current;
  const geoService = useRef(new GeographyService()).current;
  const userContext = useContext(UserContext);
  const featureFlagContext = useContext(FeatureFlagContext);
  const flyoutContext = useContext(FlyoutContext);
  const projectContext = useContext(CountyProjectContext);
  const [servicingProviders, setServicingProviders] = useState<NetNewProvider[]>([]);
  const [gainsPercent, setGainsPercent] = useState<GainsPercentEnum>(GainsPercentEnum.All);
  const [projectionProvidersState, projectionProvidersReducer] = useReducer(projectionProviderDetailsReducer, projectionProviderDetailsInitialState);
  const [selectedNetNewProvider, setSelectedNetNewProvider] = useState<ProjectionProvider>(undefined);
  const [selectedFlyoutButtonRef, setSelectedFlyoutButtonRef] = useState<number>(-1);
  const flyoutButtonRefs = useRef<React.RefObject<HTMLButtonElement>[]>([]);
  const take = 50;
  const toastId = uniqueId("distance-toast-");


  const providerPlurality = useMemo(() => {
    return selectedProviders.length === 1 ? "provider" : "providers";
  }, [selectedProviders]);

  const selectedServicingProviders = useMemo(() => {
    return servicingProviders;
  }, [servicingProviders, selectedProviders]);

  useEffect(() => {
    // prevent it from calling before the accordion opens
    if (displayedProviderData) {
      fetchProviders(displayedProviderType);
    }
    setSelectedProviders([]);
    projectContext.setShouldRefreshNetworkProviders(false);
  }, [projectContext.shouldRefreshNetNewProviders, props.viewableFields]);

  useEffect(() => setProvidersData(servicingProviders), [servicingProviders]);

  const fetchProviders = (
    shouldGetTargeted?: string,
    newSkip?: number,
    newSelectedColumn?: keyof NetNewServicingProviderTableData,
    newSortDirection?: SortDirections,
    newSearchValue?: string,
    newGainsPercent: GainsPercentEnum = gainsPercent,
    newEffectivenessValues?: EffectivenessEfficiencyValueEnum[],
    newEfficiencyValues?: EffectivenessEfficiencyValueEnum[],
    newVolumeLabels?: ClaimsActivityVolumeLabelEnum[],
    newLineOfBusiness?: ClaimsActivityLineOfBusinessTypeEnum,
    newLocationConfidenceValues?: LocationConfidenceScoresLabelEnum[],
    newMotiveAppropriatenessValues?: MotiveScoreValueEnum[],
    newNewPatientClaimsValues?: NewPatientClaimsEnum[]
  ) => {
    setLoading(true);

    if (props.project.showPciData &&
      newLineOfBusiness === undefined &&
      newVolumeLabels === undefined &&
      newEffectivenessValues === undefined &&
      newEfficiencyValues === undefined &&
      newLocationConfidenceValues === undefined &&
      newMotiveAppropriatenessValues === undefined &&
      newNewPatientClaimsValues === undefined
    ) {
      // pci-specific defaults for on load
      newEffectivenessValues = Object.values(EffectivenessEfficiencyValueEnum);
      newEfficiencyValues = Object.values(EffectivenessEfficiencyValueEnum);
      newLineOfBusiness = ClaimsActivityLineOfBusinessTypeEnum.Total;
      newVolumeLabels = [...Object.values(ClaimsActivityVolumeLabelEnum).filter(x => x !== ClaimsActivityVolumeLabelEnum.Total).map(x => x)];
      newLocationConfidenceValues = Object.values(LocationConfidenceScoresLabelEnum);
      newMotiveAppropriatenessValues = Object.values(MotiveScoreValueEnum);
      newNewPatientClaimsValues = Object.values(NewPatientClaimsEnum);
    }
    if (userContext.roles.includes(Roles.NET_NEW_PROVIDER_USER)) {
      netNewServicingProviderService.getNetNewServicingProviders(
        props.project?.projectId,
        props.runConfiguration?.projectRunConfigurationId,
        props.regionSetId,
        props.specialtyGroupCode,
        props.networkAssessmentAnalysisType,
        newGainsPercent,
        shouldGetTargeted,
        newSelectedColumn as string || selectedColumn,
        newSortDirection || sortDirection,
        newSearchValue !== undefined ? newSearchValue : lastSearchValue, // if newSearchValue is "" we want to apply that
        newEffectivenessValues !== undefined ? newEffectivenessValues : selectedEffectivenessValues,
        newEfficiencyValues !== undefined ? newEfficiencyValues : selectedEfficiencyValues,
        newVolumeLabels !== undefined ? newVolumeLabels : selectedVolumeLabels,
        newLineOfBusiness !== undefined ? newLineOfBusiness : selectedLineOfBusiness,
        newLocationConfidenceValues !== undefined ? newLocationConfidenceValues : selectedLocationConfidenceValues,
        newMotiveAppropriatenessValues !== undefined ? newMotiveAppropriatenessValues : selectedMotiveAppropriatenessValues,
        newNewPatientClaimsValues !== undefined ? newNewPatientClaimsValues : selectedNewPatientClaimsValues
      ).then((newProviderData) => {
        //TODO: Once arguments are added to the API we can just pass the displayedProviderType in instead of filtering here in UI

        setServicingProviders(newProviderData);

        if (gainsPercent != newGainsPercent) {
          setProvidersData(newProviderData); 
        }
        else if (!displayedProviderTypeDefault){
          setProviderType(newProviderData);
          setDisplayedProviderTypeDefault(true);
        }

        if (!!newGainsPercent) setGainsPercent(newGainsPercent);
        if (newSkip === 0 || !!newSkip) setSkip(newSkip);
        if (newSelectedColumn) setSelectedColumn(newSelectedColumn as string);
        if (newSortDirection) setSortDirection(newSortDirection);
        if (newSearchValue !== undefined) setLastSearchValue(newSearchValue);
        if (newEffectivenessValues !== undefined) setSelectedEffectivenessValues(newEffectivenessValues);
        if (newEfficiencyValues !== undefined) setSelectedEfficiencyValues(newEfficiencyValues);
        if (newVolumeLabels !== undefined) setSelectedVolumeLabels(newVolumeLabels);
        if (newLineOfBusiness !== undefined) setSelectedLineOfBusiness(newLineOfBusiness);
        if (newLocationConfidenceValues !== undefined) setSelectedLocationConfidenceValues(newLocationConfidenceValues);
        if (newMotiveAppropriatenessValues !== undefined) setSelectedMotiveAppropriatenessValues(newMotiveAppropriatenessValues);
        if (newNewPatientClaimsValues !== undefined) setSelectedNewPatientClaimsValues(newNewPatientClaimsValues);
        const newFlyoutRefs = [];
        for (const provider of newProviderData) {
          newFlyoutRefs[provider.providerId] = React.createRef<HTMLButtonElement>();
        }
        flyoutButtonRefs.current = newFlyoutRefs;
        setLoading(false);
      });
    }
  };

  const isFieldVisible = (fieldName: NPF) => {
    return props.viewableFields?.find((vf) => vf.backingFieldName === fieldName)?.visible;
  }

  const getProviderProfile = (provider: NetNewProvider) => {
    return (
      <ServicingProviderProfile
        {...props}
        isTerminated={false}
        netNewProvider={true}
        nextClosestProvider={false}
        provider={provider}
        providerNetworkType={props.providerType}
        isSSAToFIPSFeatureEnabled={featureFlagContext.enabledFeatureFlags.has(Features.SSA_TO_FIPS)}
      />
    )
  }

  const convertProviderToTableData = (providers: NetNewProvider[]) => {
    return providers.map<NetNewServicingProviderTableData>((p) => {
      const { specialty, specialtyCode, address, ...commonColumns } = buildCommonImpactProviderColumns(p, props.viewableFields, props.runConfiguration);

      /* eslint-disable sort-keys */
      const td: NetNewServicingProviderTableData = {
        [wafflesKeyColumnName]: p.netNewProviderProjectId.toString(), //TODO: Talk to Steven to see if necessary at all
        displayName: (
          <span>
            <FlyoutLink
              classNames="p-0 qes-button-secondary"
              content={{ body: getProviderProfile(p), title: "Provider Profile" }}
              testId="net-new-servicing-providers-panel__provider-profile-flyout"
            >
              {p.displayName}
            </FlyoutLink>
            <br />
            {isFieldVisible(NPF.NPI) && p.npi && <span className="text-muted ml15 d-block">NPI: {p.npi}</span>}
            {isFieldVisible(NPF.OrganizationName) && p.organizationName && p.entityType === EntityTypeEnum.Individual && <span className="text-muted ml15 d-block">{p.organizationName}</span>}
          </span>
        ),
        npi: p.npi,
        specialtyCode,
        specialty,
        address,
        ...commonColumns as NetNewServicingProviderTableData,
        projectionReason: (() => {
          const netNewProvider = p as unknown as NetNewProvider;
          return (
            <>
              {!netNewProvider.processed && <div className="font-weight-bold">UNPROCESSED</div>}
              {(
                <>
                  <RoleRestricted allowedRoles={[Roles.VOLUME_TARGET]}>
                    {(props.runConfiguration.isLatestPublished) && (
                      <TooltipWrapper text="View/Edit Targeted Provider Details">
                        <QESButton
                          className="side-panel-link font-weight-normal text-info p-0"
                          onClick={() => handleOpenEditProviderFlyout(netNewProvider)}
                          qesTestId={`net-new-servicing-providers-panel__target-details--edit--${netNewProvider.providerId}`}
                          qesType="none"
                          qesRef={flyoutButtonRefs.current[netNewProvider.providerId]}
                        >
                          {netNewProvider.reasonName ?
                            <span title={netNewProvider.reasonName}>{netNewProvider.reasonName}</span> : "Other"}
                        </QESButton>
                      </TooltipWrapper>
                    )}
                  </RoleRestricted>
                </>
              )}
            </>
          );
        })(),
        projectionReasonDescription: <div className="net-new-providers__projection-reason">{ellipsifyDescription((p as unknown as NetNewProvider).reasonDescription)}</div>
      };
      /* eslint-enable sort-keys */

      return td;
    });
  }

  const ellipsifyDescription = (description: string) => {
    if (!description || description.length < 250) {
      return description;
    }
    return (
      <TooltipWrapper text={description} tooltipWidth="350px">
        {description.substring(0, 250)}...
      </TooltipWrapper>
    );
  }

  const updateChangeTracker = () => {
    if (displayedProviderTypeChanged) {
      setDisplayedProviderTypeChanged(false);
    }
  }


  const handleOpenEditProviderFlyout = (netNewProvider: NetNewProvider) => {
    netNewProvider.providerId = netNewProvider.netNewProviderProjectId;
    setSelectedFlyoutButtonRef(netNewProvider.providerId);
    setSelectedNetNewProvider(netNewProvider);
    flyoutContext.setIsFlyoutVisible(true);
  };

  const accordionTitle = useMemo(() => {
    let base = `Net New Servicing Providers`;
    if (isOpen && !!displayedProviderData) {
      base += ` (${Math.min(displayedProviderData.length, totalRecords)} of ${totalRecords}${lastSearchValue ? `, filtered to ${lastSearchValue}` : ""})`;
    }
    return base;
  }, [displayedProviderData, isOpen, totalRecords]);

  const handleNetNewProviderCreatedOrUpdated = async (runProjection: boolean, hasUnprocessedProviders = true) => {
    if (runProjection) {
      props.processProviderChanges(false);
    } else {
      props.setProjectHasUnprocessedProjectionProviders(hasUnprocessedProviders);
      setSelectedProviders([]);
      fetchProviders(displayedProviderType, 0, undefined);
    }
  }

  //TODO: Look at ImpactProvidersPanel to implement correct functions in place of the empty JSX.Elements <></>
  const getBulkActionHeader = useMemo(() => {
    const allowTargetRemove = props.runConfiguration.isLatestPublished || props.runConfiguration.isProjected || props.runConfiguration.isProjectedTermed;

    const showRemoveTargeted = displayedProviderType === 'targeted' ? allowTargetRemove : false;
    const showTarget = allowTargetRemove && !showRemoveTargeted && !props.project.isEditRestricted;

    const granularity = RegionGranularity.County;
    const networkTypes = [ProviderNetworkType.NetNew];
    const regionSetIds = [props.regionSetId];
    const specialtyGroupCodes = [props.specialtyGroupCode];
    const providerAvailabilityType = displayedProviderType === "targeted" ? ExportProviderAvailabilityType.Targeted : ExportProviderAvailabilityType.All;

    const hasSelectedRecords = selectedProviders.length > 0;
    const tableType = props.providerType.charAt(0).toUpperCase() + props.providerType.substring(1);
    const targetDisabledToolTip = '';

    /* eslint-disable sort-keys */
    const adequacyFilters: AdequacyFilters = {
      active: false,
      all: false,
      approved: false,
      exceptions: false,
      gaps: false,
      opportunity: false,
      pending: false,
      changedGaps: null,
      specialtyType: null,
      viewBy: ProjectPerspective.All
    };
    const providerIds: number[] = [];
    const filters: RegionFilterItem[] = [];
    const projectURL = '';

    const selectedMemberships: number[] = [projectContext.runConfiguration.memberSetId];
    if (projectContext.isDualMembershipMode) {
      selectedMemberships.push(projectContext.compareToRunConfiguration.memberSetId);
    }

    return (
      <>
        <span className="bulk-action-group">
          <Link<Partial<ExportDataRouteState> & ReportCustomizationRouteState>
            to={{
              pathname: props.exportEndpoint,
              state: {
                activeModule: "Adequacy",
                activeNetworkType: ProviderNetworkType.NetNew,
                adequacyFilters: adequacyFilters,
                availableExportTypes: [ExportType.ServicingProviders],
                disableRegionFilters: true,
                disableSpecialtyGroupFilters: true,
                filters: filters,
                granularity,
                initialNetworkAssessmentAnalysisType: props.networkAssessmentAnalysisType,
                networkTypes,
                projectResourceId: props.project.resource.resourceId,
                projectResourceName: props.project.resource.name,
                projectURL: projectURL,
                providerAvailabilityType,
                providerIds,
                regionSetIds,
                specialtyGroupCodes,

                // new reportcustomization
                /* eslint-disable sort-keys */
                projectName: projectContext.project.resource.name,
                defaultReportType: ReportType.AdequacyServicingProvider,
                defaultProviderSource: ProviderNetworkType.NetNew,
                networkAssessmentAnalysisType: projectContext.networkAssessmentAnalysisType,
                primaryMembership: { alias: projectContext.project.memberSetAlias || projectContext.project.memberSetName, id: projectContext.project.memberSetId },
                secondaryMembership: { alias: projectContext.project.memberSetAlias2 || projectContext.project.memberSetName2, id: projectContext.project.memberSetId2 },
                selectedMemberships,
                selectedTimeslice: projectContext.runConfiguration.projectRunConfigurationId, // runConfigurationId
                isCompareMode: false,
                showPatternOfCare: projectContext.project.showPatternOfCareProviders,
                hasProspectProviders: !!projectContext.runConfiguration.prospectProviderSetId,
                hasMarketProviders: !!projectContext.runConfiguration.marketProviderSetId,
                hasNextClosestProviders: !!projectContext.runConfiguration.showNextClosestNetworkProviders,
                showAdvancedComparison: projectContext.project.showAdvancedComparison,
                showZipSummariesReport: projectContext.project.hasZipSummaries,
                showExpandableSpecialties: !!projectContext.adequacyResult?.details?.some((d) => d.canExpandSpecialties),
                /* eslint-enable */
              },
            }}
            data-testid={`${tableType}__create-report-button`}
          >
            <i className="fa fa-cloud-download" /> Create Report
          </Link>
        </span>

        <span className={`bulk-action-group ml-3`}>
          <TooltipWrapper text="Measure distance between two selected providers">
            <QESButton qesType="secondary" className="px-0" disabled={selectedProviders.length !== 2} onClick={handleDistanceClick} qesTestId={`net-new-servicing-providers-panel__distance--${ProviderNetworkType.NetNew}`}>
              <i className="fa fa-location-arrow pr5" /> Distance
            </QESButton>
          </TooltipWrapper>
        </span>

        {
          (showTarget || null) && (
            <RoleRestricted allowedRoles={[Roles.VOLUME_TARGET]}>
              <RoleRestricted deniedRoles={[Roles.SIMULATED_PROJECTION]}>
                <span className={`bulk-action-group ml-3${(hasSelectedRecords && targetDisabledToolTip === "") ? "" : " disabled"}`}>
                  <FlyoutLink
                    classNames="p-0"
                    content={{
                      body: getNetNewProviderProjectionDetails(),
                      title: `Target ${capitalizeFirstLetter(providerPlurality)}`
                    }}
                    disabled={!hasSelectedRecords || targetDisabledToolTip !== ""}
                    testId="net-new-servicing-providers-panel__target-flyout--no-preview"
                  >
                    <TooltipWrapper text={targetDisabledToolTip === '' ? `Target selected ${providerPlurality}` : targetDisabledToolTip}>
                      <i className="fa fa-bullseye" /> Target
                    </TooltipWrapper>
                  </FlyoutLink>
                </span>
              </RoleRestricted>
              <RoleRestricted allowedRoles={[Roles.SIMULATED_PROJECTION]}>
                <span className={`bulk-action-group ml-3${(hasSelectedRecords && targetDisabledToolTip === "") ? "" : " disabled"}`}>
                  <FlyoutLink
                    classNames="p-0"
                    content={{
                      body: getPreviewTargetingContent(),
                      title: `Target ${capitalizeFirstLetter(providerPlurality)}`
                    }}
                    disabled={!hasSelectedRecords || targetDisabledToolTip !== ""}
                    testId="net-new-servicing-providers-panel__target-flyout--with-preview"
                  >
                    <TooltipWrapper text={targetDisabledToolTip === '' ? `Preview and target selected ${providerPlurality}` : targetDisabledToolTip}>
                      <i className="fa fa-bullseye" /> Target
                    </TooltipWrapper>
                  </FlyoutLink>
                </span>
              </RoleRestricted>
            </RoleRestricted>
          )
        }

        <RoleRestricted allowedRoles={[Roles.VOLUME_TARGET]}>
          {(showRemoveTargeted && !props.project.isEditRestricted) && (
            <span className={`bulk-action-group ml-3${hasSelectedRecords ? "" : " disabled"}`}>
              <FlyoutLink
                classNames="p-0"
                content={{
                  body: getDeleteNetNewProvider(),
                  title: `Remove Targeted Net New Servicing ${capitalizeFirstLetter(providerPlurality)}`,
                }}
                disabled={!hasSelectedRecords}
                testId="net-new-servicing-providers-panel__delete-flyout--target"
                size="20"
              >
                <TooltipWrapper text={`Stop targeting selected Net New Servicing ${providerPlurality}`}>
                  <i className="fa fa-trash-o" /> Remove
                </TooltipWrapper>
              </FlyoutLink>
            </span>
          )}
        </RoleRestricted>
      </>
    );
  }, [selectedProviders, props.runConfiguration, props.project, displayedProviderType, projectionProvidersState]);

  function getNetNewProviderProjectionDetails() {

    const selectedNetNewProviderProjectIds: number[] = selectedProviders.map(nnp => +nnp);
    const selectedNetNewProviders: NetNewProvider[] = servicingProviders.filter(nnp => selectedNetNewProviderProjectIds.some(id => id === nnp.netNewProviderProjectId));

    return (
      <ProjectionProviderDetails
        {...props}
        hasProvidersWithoutNPI={servicingProviders.some((provider) => provider.npi === undefined)}
        networkAssessmentAnalysisType={props.runConfiguration.networkAssessmentAnalysisType}
        priorProjectionProviderProjectIds={selectedProviders.map(nnp => +nnp)}
        priorProjectionProviderType={undefined} //This is for checking between exclude and targeted. We are only targeting for nnp
        projectId={props.project.projectId}
        projectionPanelMode={ProjectionPanelMode.Create}
        projectionProviderType={ProjectionProviderType.Target}
        selectedProviders={selectedNetNewProviders}
        providerNetworkType={props.providerType}
        formState={projectionProvidersState}
        formDispatch={projectionProvidersReducer}
        onProjectionProvidersCreated={handleNetNewProviderCreatedOrUpdated}
        specialtyGroupCode={props.specialtyGroupCode}
        isZipProject={false}
      />
    );
  }

  function getDeleteNetNewProvider() {
    const selectedNetNewProviderProjectIds: number[] = selectedProviders.map(nnp => +nnp);

    return (
      <DeleteProjectionProvider
        netNewServicingProviderProjectIds={selectedNetNewProviderProjectIds}
        projectId={props.project.projectId}
        projectionProviderProjectIds={[]}
        projectionProviderType={ProjectionProviderType.Target}
        providerNetworkType={props.providerType}
        onProjectionProvidersDeleted={handleNetNewProvidersDeleted}
        wrapperComponentType="flyoutLink"
      />
    );
  }

  function renderSearchField() {
    if (props.project.showPciData) {
      return (
        <ProviderClaimsFilters
          clearFilters={displayedProviderTypeChanged}
          handleProviderSearch={handleProviderSearch}
          lastSearchValue={lastSearchValue}
          lastSelectedEffectivenessValues={selectedEffectivenessValues}
          lastSelectedEfficiencyValues={selectedEfficiencyValues}
          lastSelectedGainsPercent={gainsPercent}
          lastSelectedLineOfBusiness={selectedLineOfBusiness}
          lastSelectedVolumeLabels={selectedVolumeLabels}
          lastSelectedLocationConfidenceValues={selectedLocationConfidenceValues}
          lastSelectedMotiveAppropriatenessValues={selectedMotiveAppropriatenessValues}
          lastSelectedNewPatientClaimsValues={selectedNewPatientClaimsValues}
          showGainsPercent={true}
          tableType={TableType.NetNewProviders}
          validLoadStatus={!loading}
          showLocationConfidence={props.runConfiguration.showLocationConfidenceScores && props.project.showLocationConfidenceScores}
          updateChangeTracker={updateChangeTracker}
          showNewPatientClaims={true}
        />
      );
    } else {
      return (
        <SearchBox
          key={displayedProviderType}
          descriptiveText={`This will search each provider's name, NPI, specialty, and address.`}
          fieldsetClass="d-flex"
          inputLabel="Search Providers"
          testId={`net-new-provider-search`}
          onSubmit={(value: string) => { handleProviderSearch(value) }}
        />
      );
    }
  }

  function getPreviewTargetingContent() {
    const showAvgDist = props.runConfiguration.showDistance && props.project.showAverageDistance;
    const showAvgTime = props.runConfiguration.showTime && props.project.showAverageTime;
    const showWithTimeAndDistanceColumns = props.runConfiguration.showWithTimeAndDistanceColumns;

    const selectedNetNewProviderProjectIds: number[] = selectedProviders.map(nnp => +nnp);
    const selectedNetNewProviders: NetNewProvider[] = servicingProviders.filter(nnp => selectedNetNewProviderProjectIds.some(id => id === nnp.netNewProviderProjectId)).map(nnp => {
      nnp.providerId = nnp.netNewProviderProjectId
      return nnp;
    });

    const showNetNew = userContext.roles.includes(Roles.NET_NEW_PROVIDER_USER);

    return (
      <SimulateProviderTargeting
        processingContent={(() => getNetNewProviderProjectionDetails())()}
        projectionProviderType={ProjectionProviderType.Target}
        providerNetworkType={props.providerType}
        regionSetId={props.regionSetId}
        selectedProviders={selectedNetNewProviders}
        specialtyGroupCode={props.specialtyGroupCode}
        visibility={{ showAvgDist, showAvgTime, showNetNew, showWithTimeAndDistanceColumns }}
        user={userContext}
        formState={projectionProvidersState}
        formDispatch={projectionProvidersReducer}
        isZipProject= {false}
      />
    );
  }

  async function handleNetNewProvidersDeleted(runProjection: boolean) {
    if (runProjection) {
      props.processProviderChanges(false);
    } else {
      props.setProjectHasUnprocessedProjectionProviders(true);
      this.resetState();
    }
  }

  function handleDistanceClick() {
    if (selectedProviders.length === 2) {
      const selectedProviderData = servicingProviders.filter(sp => selectedProviders.includes(sp.netNewProviderProjectId.toString()));
      const fromProvider = selectedProviderData[0];
      const toProvider = selectedProviderData[1];

      geoService.getDrivingDistance(fromProvider.longitude, fromProvider.latitude, toProvider.longitude, toProvider.latitude).then(result => {
        toast(result, toastId);
      });
    }
  }
  function setProviderType(providerData: NetNewProvider[]) {
      if (displayedProviderType === "targeted" && providerData.length === 0) {
        setDisplayedProviderType("nonTargeted");
        setDisplayedProviderTypeChanged(true);
      } else if (displayedProviderType === "nonTargeted" && providerData.length === 0) {
        setDisplayedProviderType("targeted");
        setDisplayedProviderTypeChanged(true);
      }
  }
  function setProvidersData(providerData: NetNewProvider[]) {
    setDisplayedProviderData(convertProviderToTableData(providerData as NetNewProvider[]));
    setTotalRecords(providerData.length);
  }

  const handleProviderSearch = (searchValue: string,
    selectedEffectivenessValues?: EffectivenessEfficiencyValueEnum[],
    selectedEfficiencyValues?: EffectivenessEfficiencyValueEnum[],
    selectedVolumeLabels?: ClaimsActivityVolumeLabelEnum[],
    selectedLineOfBusiness?: ClaimsActivityLineOfBusinessTypeEnum,
    selectedLocationConfidenceValues?: LocationConfidenceScoresLabelEnum[],
    selectedMotiveAppropriatenessValues?: MotiveScoreValueEnum[],
    gainsPercent?: GainsPercentEnum,
    selectedNewPatientClaimsValues?: NewPatientClaimsEnum[]
  ) => {
    fetchProviders(
      displayedProviderType,
      undefined,
      undefined,
      undefined,
      searchValue,
      gainsPercent,
      selectedEffectivenessValues,
      selectedEfficiencyValues,
      selectedVolumeLabels,
      selectedLineOfBusiness,
      selectedLocationConfidenceValues,
      selectedMotiveAppropriatenessValues,
      selectedNewPatientClaimsValues
    );
  }

  const accordionTestId = useMemo(() => {
    return "net-new-servicing-providers-panel";
  }, [props.providerType]);

  const targetedToggle = useMemo(() => {
    fetchProviders(displayedProviderType);
    return (
      <div className="clearfix">
        <div className="pull-right">
          <IconToggle
            active={displayedProviderType === "targeted"}
            data-testid="net-new-targeted-toggle"
            onToggle={() => {
              setDisplayedProviderType(displayedProviderType === "targeted" ? "nonTargeted" : "targeted");
              setSelectedProviders([]);
              setLastSearchValue("");
              setSelectedEffectivenessValues(undefined);
              setSelectedEfficiencyValues(undefined);
              setSelectedVolumeLabels(undefined);
              setSelectedLineOfBusiness(undefined);
              setSelectedLocationConfidenceValues(undefined);
              setSelectedMotiveAppropriatenessValues(undefined);
              setSelectedNewPatientClaimsValues(undefined);
              setDisplayedProviderTypeChanged(true);
            }}
            activeIconClass={["fa", "fa-toggle-on"]} inActiveIconClass={["fa", "fa-toggle-off"]} baseClassName={["icon-toggle"]}
          >
            Targeted
          </IconToggle>
        </div>
      </div>
    );
  }, [displayedProviderType]);

  const renderDropdownAndToggles = () => {
    const networkPrefix = "net new servicing";
    const leadText = (props.accessSummary)
      ? (props.accessSummary.memberAccessPercentage < 100)
        ? (<>The following {networkPrefix} providers are capable of servicing beneficiaries.  <strong className="text-success">Green </strong>impacts access to at least {props.accessSummary.passPercentage}%, <strong className="text-warning">yellow </strong>impacts access to at least 25%, otherwise <strong className="text-danger">red</strong>.</>)
        : (<>The following {networkPrefix} providers are potential alternatives to the current servicing providers.  <strong className="text-success">Green </strong>serves at least {props.accessSummary.passPercentage}%, <strong className="text-warning">yellow </strong>serves at least 25%, otherwise <strong className="text-danger">red</strong>.</>)
      : null;

    const hasNetNewServicingProviders = servicingProviders.length > 0;
    return (
      <>
        <p className={hasNetNewServicingProviders ? "mb0" : ""}>{leadText}</p>
        {(!props.project.showPciData) && (
          <div className="d-flex justify-content-end">
            <div>
              <label className="label-s mr-3 d-inline-block" htmlFor={`claims-activity__filters--gains-percent-${ProviderNetworkType.NetNew}`}>Display</label>
              <GainsPercentFilter
                containerClassName="d-inline-block"
                gainsPercent={gainsPercent}
                hideLabel={true}
                setGainsPercent={setPercentGains}
                testId={`claims-activity__filters--gains-percent-${ProviderNetworkType.NetNew}`}
              />
            </div>

          </div>
        )}
        {targetedToggle}
      </>
    );
  }

  const setPercentGains = (gainsPercent: GainsPercentEnum) => {
    setGainsPercent(gainsPercent);
    fetchProviders(displayedProviderType, 0, undefined, undefined, undefined, gainsPercent)
  }

  return userContext.roles.includes(Roles.VOLUME_TARGET) && userContext.roles.includes(Roles.NET_NEW_PROVIDER_USER) ? (
    <>
      <AccordionContainer>
        <Accordion
          defaultOpen={false}
          title={accordionTitle}
          onPanelWillOpen={!displayedProviderData && fetchProviders}
          onPanelDidOpen={() => setIsOpen(true)}
          onPanelDidClose={() => setIsOpen(false)}
          testId={accordionTestId}
        >
          {
            displayedProviderData && props.viewableFields ?
              props.editRestricted || props.readOnly ?
                <p className="copy-text-default mb-0">Net New Servicing Provider Results are currently being
                  calculated.</p> :
                <>
                  {renderSearchField()}
                  {renderDropdownAndToggles()}
                  {
                    (
                      <>
                        <NetNewServicingProviderTable
                          accessSummary={props.accessSummary}
                          bulkActionBar={getBulkActionHeader}
                          data={displayedProviderData}
                          displayedProviderType={displayedProviderType}
                          providerType={props.providerType}
                          viewableFields={props.viewableFields}
                          handleSort={(col, direction) => {
                            fetchProviders(displayedProviderType, 0, col, direction)
                          }}
                          loading={loading}
                          onSelectedProvidersChanged={setSelectedProviders}
                          project={props.project}
                          runConfiguration={props.runConfiguration}
                          selectedProviders={selectedProviders}
                          setSkip={(newSkip: number) => fetchProviders(displayedProviderType, newSkip)}
                          skip={skip}
                          tableType={TableType.NetNewProviders}
                          take={take}
                          totalRecords={totalRecords}
                        />
                      </>
                    )}
                </> :
              <LoadingSpinner/>
          }
        </Accordion>
      </AccordionContainer>
      <Flyout
        triggerElement={flyoutButtonRefs.current[selectedFlyoutButtonRef]}
        content={{
          body: <ProjectionProviderDetails
            {...props}
              hasProvidersWithoutNPI={selectedNetNewProvider?.npi != undefined}
              networkAssessmentAnalysisType={props.runConfiguration.networkAssessmentAnalysisType}
              projectId={props.project.projectId}
              selectedProviders={selectedServicingProviders}
              projectionPanelMode={ProjectionPanelMode.Edit}
              projectionProvider={selectedNetNewProvider}
              projectionProviderType={ProjectionProviderType.Target}
              providerNetworkType={props.providerType}
              formState={projectionProvidersState}
              formDispatch={projectionProvidersReducer}
              onProjectionProviderUpdated={handleNetNewProviderCreatedOrUpdated}
              specialtyGroupCode={props.specialtyGroupCode}
              isZipProject={false}
              />,
          title: "Target Details",
        }}
      />
    </>
  ) : <></>;
}

const WrappedNetNewServicingProvidersPanel: React.FC<NetNewServicingProvidersPanelProps> = (props: NetNewServicingProvidersPanelProps) => {
  return (
    <FlyoutContextProvider>
      <NetNewServicingProvidersPanel {...props} />
    </FlyoutContextProvider>
  )
};

export { WrappedNetNewServicingProvidersPanel as NetNewServicingProvidersPanel };
